import { MentorshipDetails } from "@/lib/api/mentorships";
import {
  ShareableEntity,
  useShareableEntities,
  useShareableEntity,
} from "@/lib/api/shareable-entities";
import { HubListView } from "./hub-list-view";
import { CreateNoteView } from "./notes/create-note-view";
import { PreviewNoteView } from "./notes/preview-note-view";
import { ViewNoteView } from "./notes/view-note-view";
import { CreateMomentView, PreviewMomentView } from "./moments";
import { ViewMomentView } from "./moments/view-moment-view";
import {
  CustomSwapView,
  SelectSwapPromptView,
  SurpriseSwapView,
  UploadSwapView,
  ViewSwapView,
} from "./swaps";
import { useHubEntityNavigation } from "./use-hub-entity-navigation";
import { useHubStore } from "./use-hub-store";
import { useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { useAbility } from "@/lib/casl/ability-context";
import { useAuth } from "@/components/auth/auth-context";
import { findActivityConfigByHubView } from "@/lib/activities/registry";

/** Legacy list, or dismiss Hub when hosted inside the Action shell. */
function HubListOrDismiss({ mentorship }: { mentorship: MentorshipDetails }) {
  const embedMode = useHubStore((s) => s.embedMode);
  const close = useHubStore((s) => s.close);

  useEffect(() => {
    if (embedMode) close();
  }, [embedMode, close]);

  if (embedMode) return null;
  return <HubListView mentorship={mentorship} />;
}

interface HubRouterProps {
  mentorship: MentorshipDetails;
}

const CREATE_VIEWS = [
  "create-note",
  "preview-note",
  "create-moment",
  "preview-moment",
  "create-swap",
  "surprise-swap",
  "custom-swap",
  "upload-swap",
];

export function HubRouter({ mentorship }: HubRouterProps) {
  const ability = useAbility();
  const { user } = useAuth();
  const canCreate = ability.can("create", "shareable-entities");
  const userIsMentor = user?.id === mentorship.mentor.id;

  const currentView = useHubStore((state) => state.currentView);
  const selectedNoteId = useHubStore((state) => state.selectedNoteId);
  const selectedIndex = useHubStore((state) => state.selectedIndex);
  const navigateToView = useHubStore((state) => state.navigateToView);
  const activeTab = useHubStore((state) => state.activeTab);
  const activeFilter = useHubStore((state) => state.activeFilter);

  // Admin viewers have no per-user read state, so force "all" to match HubListView
  const effectiveTab = userIsMentor ? activeTab : "all";
  const readStatus = effectiveTab === "new" ? "unread" : "all";
  const filter =
    effectiveTab === "all" && activeFilter !== "all" ? activeFilter : undefined;

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useShareableEntities(mentorship.mentee.id, filter, readStatus);

  const entities = useMemo(() => {
    return data?.pages.flatMap((page) => page.entities) ?? [];
  }, [data]);

  const totalCount = useMemo(() => {
    if (!data?.pages || data.pages.length === 0) return 0;
    return data.pages[0]?.total_count ?? 0;
  }, [data]);

  const entityFromList = selectedNoteId
    ? entities.find((entity) => entity.id === selectedNoteId)
    : null;

  const { data: fetchedEntity, isLoading: isFetchingEntity } =
    useShareableEntity(
      entityFromList ? "" : selectedNoteId ?? "",
      mentorship.mentee.id,
    );

  const selectedEntity: ShareableEntity | null =
    entityFromList ?? fetchedEntity ?? null;

  const { onPrevious, onNext, onNavigateToList } = useHubEntityNavigation({
    entities,
    selectedEntity,
    selectedNoteId,
    currentView,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    navigateToView,
  });

  // Redirect create/preview views to list for users who lack create permission
  // OR who are not the mentor of this specific mentorship (admin viewers)
  const isCreateView = CREATE_VIEWS.includes(currentView);

  if (isCreateView && (!canCreate || !userIsMentor)) {
    return <HubListOrDismiss mentorship={mentorship} />;
  }

  const activityMatch = findActivityConfigByHubView(currentView);
  if (activityMatch) {
    if (!selectedNoteId) {
      return <HubListOrDismiss mentorship={mentorship} />;
    }
    if (isFetchingEntity && !entityFromList) {
      return (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      );
    }
    const entityIndex = selectedEntity
      ? entities.findIndex((entity) => entity.id === selectedEntity.id) + 1
      : 0;
    const HubView = activityMatch.config.HubView;
    return (
      <HubView
        entityId={selectedNoteId}
        entity={selectedEntity}
        mentorship={mentorship}
        mentorAvatarUrl={mentorship.mentor.profile_picture_url}
        teenAvatarUrl={mentorship.mentee.profile_picture_url}
        currentIndex={entityIndex || undefined}
        totalEntities={totalCount || undefined}
        isLoadingNextPage={isFetchingNextPage}
        activeTab={effectiveTab}
        initialIndex={selectedIndex}
        onPrevious={onPrevious}
        onNext={onNext}
        onNavigateToList={onNavigateToList}
        readOnly={!userIsMentor}
      />
    );
  }

  switch (currentView) {
    case "create-note":
      return <CreateNoteView mentorship={mentorship} />;
    case "preview-note":
      return <PreviewNoteView mentorship={mentorship} />;
    case "create-moment":
      return <CreateMomentView mentorship={mentorship} />;
    case "preview-moment":
      return <PreviewMomentView mentorship={mentorship} />;
    case "create-swap":
      return <SelectSwapPromptView />;
    case "surprise-swap":
      return <SurpriseSwapView mentorship={mentorship} />;
    case "custom-swap":
      return <CustomSwapView mentorship={mentorship} />;
    case "upload-swap":
      return <UploadSwapView mentorship={mentorship} />;
    case "view-swap":
    case "view-moment":
    case "view-note": {
      if (isFetchingEntity && !entityFromList) {
        return (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
          </div>
        );
      }

      if (!selectedEntity) {
        return <HubListOrDismiss mentorship={mentorship} />;
      }

      const entityIndex =
        entities.findIndex((entity) => entity.id === selectedEntity.id) + 1;

      const viewProps = {
        entity: selectedEntity,
        menteeId: mentorship.mentee.id,
        mentorId: mentorship.mentor.id,
        mentorAvatarUrl: mentorship.mentor.profile_picture_url,
        currentIndex: entityIndex || undefined,
        teenAvatarUrl: mentorship.mentee.profile_picture_url,
        totalEntities: totalCount || undefined,
        activeTab: effectiveTab,
        onPrevious,
        onNext,
        isLoadingNextPage: isFetchingNextPage,
        onNavigateToList,
      };

      switch (currentView) {
        case "view-swap":
          return <ViewSwapView {...viewProps} />;
        case "view-moment":
          return <ViewMomentView {...viewProps} />;
        case "view-note":
          return (
            <ViewNoteView {...viewProps} totalNotes={viewProps.totalEntities} />
          );
      }
      return <HubListOrDismiss mentorship={mentorship} />;
    }
    case "list":
    default:
      return <HubListOrDismiss mentorship={mentorship} />;
  }
}
